import { useQuery } from "@tanstack/react-query";

import { API } from "../constant/api";
import useAuthAxios from "../utils/baseAxios";

/**
 * Resolves a saved account's `bankCode` to a display name ("Guaranty Trust
 * Bank"). Accounts only ever store the code, so the name has to be looked up
 * against the bank lists.
 *
 * Two lists, because accounts were saved against both over time: the current
 * PalmPay list keys on `bankCode`/`bankName`, the legacy one on `code`/`name`.
 * Both are cached for 30 minutes under shared keys, so the round trips are paid
 * once no matter how many callers ask.
 */
export const useBankName = () => {
  const axiosInstance = useAuthAxios();

  const { data: allBanks, error: banksError } = useQuery({
    queryKey: ["all-banks"],
    queryFn: async () => {
      const response = await axiosInstance.get(API.getBanks);
      return response.data;
    },
    staleTime: 30 * 60_000,
    retry: 1,
  });

  const { data: allOldBanks, error: oldBanksError } = useQuery({
    queryKey: ["all-old-banks"],
    queryFn: async () => {
      const response = await axiosInstance.get(API.getOldBanks);
      return response.data;
    },
    staleTime: 30 * 60_000,
    retry: 1,
  });

  const bankName = (code?: string): string | undefined => {
    if (!code) return undefined;
    const match =
      allBanks?.data?.find((bank: any) => bank.bankCode === code) ||
      allOldBanks?.data?.find((bank: any) => bank.code === code);
    return match?.bankName || match?.name;
  };

  return {
    bankName,
    /**
     * Whichever list failed first. A missing name only costs a line of detail,
     * so callers are free to ignore this — surface it where the bank is the
     * point of the screen, stay quiet where it's incidental.
     */
    error: (banksError ?? oldBanksError) as any,
  };
};
